import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../lib/api";
import { preparationApi, type TournamentSetupPayload } from "../../lib/preparationApi";

function initial(data: TournamentSetupPayload) {
  return {
    name: data.tournament.name,
    season: data.tournament.season,
    startDate: data.tournament.startDate,
    endDate: data.tournament.endDate,
    status: data.tournament.status,
    location: data.tournament.location,
    country: data.tournament.country,
    fieldBuffer: data.tournament.fieldBuffer,
    teeTimesUrl: data.tournament.teeTimesUrl,
    notes: data.tournament.notes,
  };
}

export function TournamentDetailsEditor({ tournamentId, data }: { tournamentId: string; data: TournamentSetupPayload }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => initial(data));
  const reset = () => setDraft(initial(data));

  const save = useMutation({
    mutationFn: () => preparationApi.saveTournamentDetails(tournamentId, { expectedRevision: data.configRevision, ...draft }),
    onSuccess: (next) => {
      queryClient.setQueryData(["tournament-setup", tournamentId], next);
      void queryClient.invalidateQueries({ queryKey: ["tournaments"] });
      void queryClient.invalidateQueries({ queryKey: ["round-workspace", tournamentId] });
      void queryClient.invalidateQueries({ queryKey: ["generator-preflight", tournamentId] });
      void queryClient.invalidateQueries({ queryKey: ["dpworld-source", tournamentId] });
      setOpen(false);
    },
  });

  const error = save.error instanceof ApiError ? save.error.message : save.error instanceof Error ? save.error.message : "";
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial(data));

  if (!open) return <button type="button" className="secondary-button" onClick={() => { reset(); setOpen(true); }}>Edytuj dane</button>;

  return (
    <div className="tournament-settings-layer" role="presentation">
      <button className="tournament-settings-backdrop" type="button" aria-label="Zamknij edytor" onClick={() => setOpen(false)} />
      <section className="tournament-settings-dialog" role="dialog" aria-modal="true" aria-label="Edycja turnieju">
        <header className="tournament-settings-header">
          <div><span className="eyebrow">STEROWANIE TURNIEJEM</span><h3>{data.tournament.name}</h3><p>Zmiany zapisują się bezpośrednio w konfiguracji V2 · revision r{data.configRevision}</p></div>
          <button type="button" className="tournament-settings-close" onClick={() => setOpen(false)}>×</button>
        </header>
        <div className="tournament-settings-scroll">
          <section className="tournament-settings-panel">
            <div className="tournament-settings-panel-head"><div><h4>Dane turnieju</h4><p>Nazwa, daty, status i parametry używane przez generator.</p></div></div>
            <div className="tournament-settings-form">
              <label className="span-2"><span>Nazwa</span><input value={draft.name} onChange={(event) => setDraft((value) => ({ ...value, name: event.target.value }))} /></label>
              <label><span>Sezon</span><input type="number" min={2020} max={2100} value={draft.season} onChange={(event) => setDraft((value) => ({ ...value, season: Number(event.target.value) }))} /></label>
              <label><span>Status</span><select value={draft.status} onChange={(event) => setDraft((value) => ({ ...value, status: event.target.value }))}><option value="planned">Planowany</option><option value="active">Aktywny</option><option value="completed">Zakończony</option><option value="archived">Archiwalny</option></select></label>
              <label><span>Data R1</span><input type="date" value={draft.startDate.slice(0, 10)} onChange={(event) => setDraft((value) => ({ ...value, startDate: event.target.value }))} /></label>
              <label><span>Data końca</span><input type="date" value={draft.endDate.slice(0, 10)} onChange={(event) => setDraft((value) => ({ ...value, endDate: event.target.value }))} /></label>
              <label><span>Bufor na polu [min]</span><input type="number" min={0} max={120} value={draft.fieldBuffer} onChange={(event) => setDraft((value) => ({ ...value, fieldBuffer: Number(event.target.value) }))} /></label>
              <label><span>Kraj</span><input value={draft.country} onChange={(event) => setDraft((value) => ({ ...value, country: event.target.value }))} /></label>
              <label className="span-2"><span>Lokalizacja / venue</span><input value={draft.location} onChange={(event) => setDraft((value) => ({ ...value, location: event.target.value }))} /></label>
              <label className="span-2"><span>Oficjalny link Tee Times / override</span><input type="url" value={draft.teeTimesUrl} onChange={(event) => setDraft((value) => ({ ...value, teeTimesUrl: event.target.value }))} placeholder="Puste = AUTO DP World Tour" /></label>
              <label className="span-4"><span>Notatka</span><textarea rows={3} value={draft.notes} onChange={(event) => setDraft((value) => ({ ...value, notes: event.target.value }))} /></label>
            </div>
            <div className="tournament-settings-hint"><strong>Tee Times AUTO</strong><span>Normalnie pole linku zostawiasz puste. Generator sam buduje źródło dla wybranego eventu i rundy.</span>{draft.teeTimesUrl ? <button className="text-button" type="button" onClick={() => setDraft((value) => ({ ...value, teeTimesUrl: "" }))}>Usuń override</button> : null}</div>
          </section>
          {error ? <div className="prep-editor-error">{error}</div> : null}
        </div>
        <footer className="tournament-settings-footer"><button type="button" className="secondary-button" onClick={() => setOpen(false)}>Anuluj</button><button type="button" className="primary-button" disabled={!dirty || save.isPending || !draft.name.trim()} onClick={() => save.mutate()}>{save.isPending ? "Zapisuję…" : "Zapisz dane turnieju"}</button></footer>
      </section>
    </div>
  );
}
